// Mass Text in ChatLog (Kloepfer K3-10, 2026-10-19): a room whose people are
// the project Directory — outside contacts who never log in. A post goes to
// everyone as a text and an email (like a BCC); each answer opens a private
// thread with that one person, answered one at a time.
//
// The ChatLog panel (toolbox_chat_controller) owns the screens, header and
// composer; this class paints the Mass Text room and its threads into them
// and talks to /projects/:id/chat/mass_text*. Every word on screen comes from
// the server, already translated.

export const MASS_TEXT_ROOM = "masstext"

const MEGAPHONE = '<svg class="__SIZE__" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 11v2a1 1 0 0 0 1 1h2l5 4V6L6 10H4a1 1 0 0 0-1 1z"/><path d="M15 9.5a3.5 3.5 0 0 1 0 5M17.5 7a7 7 0 0 1 0 10"/></svg>'

export function massTextIcon(size = "w-5 h-5") {
  return MEGAPHONE.replace("__SIZE__", size)
}

const TAG_STYLES = {
  channel: "bg-[#eefaf0] text-[#2fa04e]",
  sent: "bg-white border border-gray-200 text-gray-600",
  note: "bg-white border border-gray-200 text-gray-600",
  failed: "bg-red-50 border border-red-200 text-red-600"
}

export class MassTextRoom {
  constructor(ctl, config) {
    this.ctl = ctl
    this.cfg = config || {}
    this.t = this.cfg.i18n || {}
    this.thread = this._readThread()
    this.room = null
    this.threadData = null
    this.snapshot = null
    this.sending = false
    this.expanded = {}
  }

  get enabled() { return !!this.cfg.enabled }
  get active() { return this.ctl.room === MASS_TEXT_ROOM }

  // ── Where you are: the room or one person's thread (survives a reload) ──
  _threadKey() { return `chatlog-mt-thread-${this.ctl.projectIdValue}` }

  _readThread() {
    try { return JSON.parse(sessionStorage.getItem(this._threadKey()) || "null") } catch { return null }
  }

  _saveThread() {
    try {
      if (this.thread) sessionStorage.setItem(this._threadKey(), JSON.stringify(this.thread))
      else sessionStorage.removeItem(this._threadKey())
    } catch { /* storage off — the room just opens on its list */ }
  }

  // Coming in from the chats list or the Directory: the room, not a thread.
  enterRoom() {
    this.thread = null
    this.threadData = null
    this.snapshot = null
    this._saveThread()
  }

  openThread(postId, entryId) {
    this.thread = { post_id: Number(postId), entry_id: Number(entryId) }
    this.threadData = null
    this.snapshot = null
    this._saveThread()
    this.ctl._loadRoom()
  }

  closeThread() {
    this.thread = null
    this.threadData = null
    this.snapshot = null
    this._saveThread()
    this.ctl._loadRoom()
  }

  // ── Header: "Mass Text" + who it reaches, or the person in the thread ──
  paintHeader() {
    const ctl = this.ctl
    if (ctl.hasPanelTitleTarget) {
      const title = ctl.panelTitleTarget
      const person = this.thread && this.threadData
      const name = person ? this.threadData.title : (this.room?.label || this.t.room_label || "")
      const sub = person ? this.threadData.subtitle : (this.room?.subtitle || "")
      title.innerHTML = `<span class="min-w-0 text-left"><span class="block text-[13px] font-semibold text-gray-700 truncate">${this._esc(name)}</span>` +
        (sub ? `<span class="block text-[13px] font-normal text-gray-400 truncate">${this._esc(sub)}</span>` : "") + "</span>"
      title.classList.remove("cursor-pointer", "hover:text-gray-900")
      title.classList.add("cursor-default")
      title.title = ""
    }
    // In a thread the back arrow goes to the room, and says so.
    if (ctl.hasBackButtonTarget) {
      const back = ctl.backButtonTarget
      back.dataset.defaultTitle ||= back.title
      back.title = this.thread && this.t.back ? this.t.back : back.dataset.defaultTitle
    }
    if (ctl.hasChatIconTarget) {
      const slot = ctl.chatIconTarget
      if (this.thread && this.threadData) {
        slot.className = "inline-flex w-8 h-8 rounded-full items-center justify-center shrink-0 overflow-hidden"
        slot.innerHTML = this._initialsAvatar(this.threadData.initials, this.threadData.color, "w-8 h-8")
      } else {
        slot.className = "inline-flex w-6 h-6 rounded-full items-center justify-center shrink-0 bg-[#eefaf0] text-[#2fa04e]"
        slot.innerHTML = massTextIcon("w-4 h-4")
      }
    }
  }

  // Another room is on screen: undo what Mass Text changed in the shared
  // header and composer.
  leave() {
    const ctl = this.ctl
    if (ctl.hasComposerHintTarget) ctl.composerHintTarget.classList.replace("text-[13px]", "text-[11px]")
    if (ctl.hasBackButtonTarget && ctl.backButtonTarget.dataset.defaultTitle) {
      ctl.backButtonTarget.title = ctl.backButtonTarget.dataset.defaultTitle
    }
  }

  // Before the room (or thread) answers, paint what we last knew of it.
  paintComposerFromCache() {
    const data = this.thread ? this.threadData : this.room
    this._paintComposer(data?.composer || { placeholder: "", hint: "" }, this.thread ? true : !data?.empty)
  }

  // Mass Text is never archived: no read-only bar, and no composer while
  // the Directory has nobody to reach.
  paintComposerState() {
    const ctl = this.ctl
    if (ctl.hasArchivedBarTarget) {
      ctl.archivedBarTarget.classList.add("hidden")
      ctl.archivedBarTarget.classList.remove("flex")
    }
    const data = this.thread ? this.threadData : this.room
    if (ctl.hasComposerBoxTarget) ctl.composerBoxTarget.classList.toggle("hidden", !this.thread && !!data?.empty)
  }

  // Placeholder + hint for the room or the thread; no composer at all while
  // the Directory has nobody to reach.
  _paintComposer(composer, canPost) {
    const ctl = this.ctl
    if (ctl.hasComposerBoxTarget) ctl.composerBoxTarget.classList.toggle("hidden", !canPost)
    if (!composer) return
    if (ctl.hasInputTarget) ctl.inputTarget.placeholder = composer.placeholder || ""
    if (ctl.hasComposerHintTarget) {
      // New words, house size: 13px (the panel's own hint is smaller).
      ctl.composerHintTarget.classList.replace("text-[11px]", "text-[13px]")
      ctl.composerHintTarget.innerHTML = this._esc(composer.hint || "").replace(/\/note/g, '<b class="font-semibold">/note</b>')
    }
  }

  // ── Loading + polling ──────────────────────────────────────────────────
  async load() {
    this.snapshot = null
    if (this.thread) await this._fetchThread(true)
    else await this._fetchRoom(true)
  }

  async poll() {
    if (this.sending) return
    if (this.thread) await this._fetchThread(false)
    else await this._fetchRoom(false)
  }

  async _fetchRoom(first) {
    const thread = this.thread
    try {
      const resp = await fetch(this.ctl._url("mass_text"), { headers: { Accept: "application/json" }, credentials: "same-origin" })
      if (!resp.ok) return
      const data = await resp.json()
      if (!this.active || this.thread !== thread) return
      this.room = data
      const snap = JSON.stringify(data)
      if (snap === this.snapshot) return
      this.snapshot = snap
      this._renderRoom(data, first)
      this.paintHeader()
      this._paintComposer(data.composer, !data.empty)
      this.markRead()
    } catch { /* the next tick retries */ }
  }

  async _fetchThread(first) {
    const thread = this.thread
    const query = new URLSearchParams({ post_id: thread.post_id, entry_id: thread.entry_id })
    try {
      const resp = await fetch(this.ctl._url(`mass_text_thread?${query}`), { headers: { Accept: "application/json" }, credentials: "same-origin" })
      // The post or the person is gone (removed from the Directory): back to the room.
      if (resp.status === 404) { if (this.thread === thread) this.closeThread(); return }
      if (!resp.ok) return
      const data = await resp.json()
      if (!this.active || this.thread !== thread) return
      this.threadData = data
      const snap = JSON.stringify(data)
      if (snap === this.snapshot) return
      this.snapshot = snap
      this._renderThread(data, first)
      this.paintHeader()
      this._paintComposer(data.composer, true)
      this.markRead()
    } catch { /* the next tick retries */ }
  }

  async markRead() {
    try {
      await fetch(this.ctl._url("mass_text_read"), {
        method: "POST", headers: this.ctl._headers(), credentials: "same-origin", body: "{}"
      })
      this.ctl._zeroBadge(MASS_TEXT_ROOM)
    } catch { /* fine */ }
  }

  // ── The room ───────────────────────────────────────────────────────────
  _renderRoom(data, first) {
    const list = this.ctl.listTarget
    const atBottom = first || list.scrollHeight - list.scrollTop - list.clientHeight < 80
    const nodes = [this._infoBox(data.intro?.title, data.intro?.body)]
    let day = null
    ;(data.posts || []).forEach(post => {
      if (post.day_label !== day) {
        nodes.push(this._daySeparator(post.day_label))
        day = post.day_label
      }
      nodes.push(this._postBubble(post))
      if ((post.replies || []).length) nodes.push(this._repliesCard(post))
    })
    if (data.empty) nodes.push(this._emptyBox(data.empty))
    list.replaceChildren(...nodes)
    this.ctl.emptyTarget.classList.add("hidden")
    if (atBottom) this.ctl._scrollDown(true)
  }

  _infoBox(title, body) {
    const el = document.createElement("div")
    el.className = "shrink-0 rounded-lg bg-white border border-gray-200 px-3 py-2 text-[13px] text-gray-600 leading-relaxed"
    el.innerHTML = `<b class="text-gray-800">${this._esc(title)}</b> ${this._esc(body)}`
    return el
  }

  _daySeparator(label) {
    const el = document.createElement("div")
    el.className = "flex items-center gap-2 text-[13px] font-semibold text-gray-400"
    el.innerHTML = `<span class="flex-1 h-px bg-gray-300"></span>${this._esc(label)}<span class="flex-1 h-px bg-gray-300"></span>`
    return el
  }

  // Nobody in the Directory to reach: point at the Directory card instead of
  // offering a composer that can't send anywhere.
  _emptyBox(empty) {
    const el = document.createElement("div")
    el.className = "shrink-0 rounded-xl border border-dashed border-gray-300 bg-white px-4 py-5 text-center text-[13px] text-gray-500 leading-relaxed"
    el.innerHTML = `<div class="mx-auto mb-2 w-10 h-10 rounded-full bg-[#eefaf0] text-[#2fa04e] flex items-center justify-center">${massTextIcon()}</div>
      <p class="font-semibold text-gray-700">${this._esc(empty.title)}</p>
      <p class="mt-1">${this._esc(empty.body)}</p>
      <a href="${this._esc(empty.url)}" class="inline-flex items-center gap-1 mt-3 px-3 py-1.5 rounded-lg bg-[#2fa04e] hover:bg-[#3ba94a] text-white text-[13px] font-medium">${this._esc(empty.link)}</a>`
    return el
  }

  _postBubble(post) {
    const wrap = document.createElement("div")
    wrap.dataset.mtPost = post.id
    wrap.className = `flex gap-2 max-w-[94%] ${post.mine ? "self-end flex-row-reverse" : ""}`
    const chips = (post.chips || []).map(chip =>
      `<span class="inline-flex items-center gap-1 bg-white border border-gray-200 rounded-full px-2 py-0.5 font-semibold text-gray-600">${this._esc(chip.text)}` +
      (chip.failed ? `<span class="text-red-600">· ${this._esc(chip.failed)}</span>` : "") + "</span>"
    ).join("")
    wrap.innerHTML = `${this._initialsAvatar(post.user?.initials, post.user?.id)}
      <div class="relative border rounded-xl px-3 py-2 text-[13px] leading-relaxed min-w-0 ${post.mine ? "bg-[#dcf8c6] border-[#c3e6ab]" : "bg-white border-gray-200"}">
        <div class="text-[13px] font-bold text-gray-700 mb-0.5">${this._esc(post.author)} <span class="font-medium text-gray-400 ml-1">${this._esc(post.at_label)}</span></div>
        <div class="text-gray-800 break-words">${this._format(post.body)}</div>
        ${chips ? `<div class="mt-1.5 text-[13px] flex items-center gap-1.5 flex-wrap">${chips}</div>` : ""}
      </div>`
    return wrap
  }

  // "Replies · 2 people · only your team sees these": each person who
  // answered this post, their newest words, and a way into their thread.
  _repliesCard(post) {
    const card = document.createElement("div")
    // shrink-0: in the scrolling flex column an overflow-hidden card would
    // otherwise be squeezed and clip its last rows.
    card.className = "shrink-0 rounded-xl border border-gray-200 bg-white overflow-hidden"
    const rows = post.replies.map((r, index) => {
      const last = index === post.replies.length - 1
      const answer = r.answer
        ? `<div class="mt-1.5 ml-10 rounded-lg ${r.answer.note ? "bg-white border border-dashed border-gray-300" : "bg-[#dcf8c6] border border-[#c3e6ab]"} px-2.5 py-1.5 text-[13px] text-gray-800 leading-relaxed break-words">` +
          `<span class="font-semibold text-gray-500">${this._esc(r.answer.label)}</span><br>${this._format(r.answer.body)}</div>`
        : `<div class="mt-1.5 ml-10 flex flex-wrap items-center gap-x-2 gap-y-1"><span class="inline-flex items-center bg-white border border-gray-300 rounded-lg px-2.5 py-1 text-[13px] font-semibold text-gray-700">${this._esc(r.reply_label)}</span>` +
          (r.reply_hint ? `<span class="text-[13px] text-gray-400">${this._esc(r.reply_hint)}</span>` : "") + "</div>"
      return `<div role="button" tabindex="0" class="js-mt-open block px-3 py-2.5 ${last ? "" : "border-b border-gray-100"} hover:bg-gray-50 cursor-pointer focus:outline-none focus-visible:bg-gray-50"
                   data-post-id="${r.post_id}" data-entry-id="${r.entry_id}">
          <div class="flex items-center gap-2">
            ${this._initialsAvatar(r.initials, r.color)}
            <div class="min-w-0 flex-1 flex items-center gap-1.5">
              <span class="min-w-0 text-[13px] font-bold text-gray-800 truncate">${this._esc(r.name)}` +
              (r.company ? ` <span class="font-medium text-gray-400">${this._esc(r.company)}</span>` : "") + `</span>
              <span class="shrink-0 inline-flex items-center rounded-full ${TAG_STYLES.channel} px-1.5 py-0.5 text-[13px] font-semibold">${this._esc(r.tag)}</span>
            </div>
            <span class="text-[13px] text-gray-400 shrink-0">${this._esc(r.at_label)}</span>
            <span class="text-[13px] font-semibold text-[#2fa04e] shrink-0 ml-1">${this._esc(this.t.open || "")}</span>
          </div>
          <div class="mt-1.5 ml-10 text-[13px] text-gray-800 leading-relaxed break-words">${this._format(r.latest)}</div>
          ${answer}
        </div>`
    }).join("")
    card.innerHTML = `<div class="px-3 py-2 text-[13px] font-bold text-gray-500 uppercase tracking-wide bg-gray-50 border-b border-gray-200">${this._esc(post.replies_title)}</div>${rows}`
    const open = row => this.openThread(row.dataset.postId, row.dataset.entryId)
    card.addEventListener("click", event => {
      const row = event.target.closest(".js-mt-open")
      if (row) open(row)
    })
    card.addEventListener("keydown", event => {
      const row = event.target.closest(".js-mt-open")
      if (row && (event.key === "Enter" || event.key === " ")) { event.preventDefault(); open(row) }
    })
    return card
  }

  // ── One person's thread ────────────────────────────────────────────────
  _renderThread(data, first) {
    const list = this.ctl.listTarget
    const atBottom = first || list.scrollHeight - list.scrollTop - list.clientHeight < 80
    const nodes = [this._infoBox(data.note?.title, data.note?.body), this._quotedPost(data.post_id, data.post)]
    ;(data.messages || []).forEach(m => nodes.push(m.direction === "system" ? this._systemLine(m) : this._threadBubble(m)))
    list.replaceChildren(...nodes)
    this.ctl.emptyTarget.classList.add("hidden")
    if (atBottom) this.ctl._scrollDown(true)
  }

  // The post this thread answers, small, with "Show all".
  _quotedPost(postId, post) {
    const el = document.createElement("div")
    el.className = "shrink-0 rounded-lg border border-dashed border-gray-300 bg-gray-50 px-3 py-2 text-[13px] text-gray-500 leading-relaxed break-words"
    el.innerHTML = `<span class="font-semibold text-gray-600">${this._esc(post?.label)}</span><br>`
    const body = document.createElement("span")
    el.appendChild(body)
    const paint = () => { body.innerHTML = this._format(this.expanded[postId] ? post.body : post.short) }
    paint()
    if (post?.long) {
      const toggle = document.createElement("button")
      toggle.type = "button"
      toggle.className = "ml-1 text-[#2fa04e] font-semibold hover:underline cursor-pointer"
      toggle.textContent = this.expanded[postId] ? post.show_less : post.show_all
      toggle.addEventListener("click", () => {
        this.expanded[postId] = !this.expanded[postId]
        paint()
        toggle.textContent = this.expanded[postId] ? post.show_less : post.show_all
      })
      el.appendChild(toggle)
    }
    return el
  }

  // Their words on the left; the team's answers and notes on the right.
  _threadBubble(m) {
    const wrap = document.createElement("div")
    wrap.dataset.mtLine = m.id || ""
    wrap.className = `flex gap-2 max-w-[94%] ${m.team ? "self-end flex-row-reverse" : ""}`
    const tag = m.tag
      ? `<span class="inline-flex items-center rounded-full ${TAG_STYLES[m.tag_kind] || TAG_STYLES.sent} px-1.5 py-0.5 font-semibold ml-1">${this._esc(m.tag)}</span>`
      : ""
    const files = (m.files || []).map(f => f.image
      ? `<a href="${this._esc(f.url)}" target="_blank" rel="noopener"><img src="${this._esc(f.url)}" alt="${this._esc(f.name)}" class="rounded-lg max-w-[220px] max-h-[180px] object-cover my-1 border border-gray-200" loading="lazy"></a>`
      : `<a href="${this._esc(f.url)}" target="_blank" rel="noopener" class="inline-flex items-center gap-2 bg-gray-100 border border-gray-200 rounded-lg px-2.5 py-1.5 mt-1 text-[13px] font-semibold text-gray-700 hover:bg-gray-200">${this._esc(f.name)}</a>`
    ).join("")
    wrap.innerHTML = `${this._initialsAvatar(m.initials, m.color)}
      <div class="relative border rounded-xl px-3 py-2 text-[13px] leading-relaxed min-w-0 ${m.team ? "bg-[#dcf8c6] border-[#c3e6ab]" : "bg-white border-gray-200"} ${m.pending ? "opacity-60" : ""}">
        <div class="text-[13px] font-bold text-gray-700 mb-0.5">${this._esc(m.name)} <span class="font-medium text-gray-400 ml-1">${this._esc(m.at_label)}</span>${tag}</div>
        ${m.body ? `<div class="text-gray-800 break-words">${this._format(m.body)}</div>` : ""}
        ${files}
      </div>`
    return wrap
  }

  _systemLine(m) {
    const el = document.createElement("div")
    el.className = "text-center text-[13px] text-gray-400"
    el.textContent = [m.text, m.at_label].filter(Boolean).join(" · ")
    return el
  }

  // ── Sending ────────────────────────────────────────────────────────────
  async send() {
    if (this.sending || !this.ctl.hasInputTarget) return
    const body = this.ctl.inputTarget.value.trim()
    if (!body) return
    if (this.thread) return this._sendReply(body)
    return this._sendPost(body)
  }

  // The post to everyone. It costs money and can't be taken back, so it
  // asks first: "Send to 14 people? 14 by text · 13 by email".
  async _sendPost(body) {
    const room = this.room
    if (!room || room.empty) return
    const ok = await this.ctl._confirm({ title: room.confirm.title, message: room.confirm.message, label: room.confirm.button })
    if (!ok) return

    this.sending = true
    const input = this.ctl.inputTarget
    const temp = this._postBubble({ id: 0, mine: true, author: this.t.you || "", body: body,
                                    at_label: this.t.sending || "", user: { initials: "•", id: this.ctl.currentUserIdValue } })
    temp.classList.add("opacity-60")
    this.ctl.listTarget.appendChild(temp)
    this.ctl._scrollDown()
    input.value = ""
    try {
      const resp = await fetch(this.ctl._url("mass_text"), {
        method: "POST", headers: this.ctl._headers(), credentials: "same-origin", body: JSON.stringify({ body: body })
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok || !data.ok) throw new Error(data.error || this.t.error_send)
      if (data.toast) this._toast(data.toast)
      this.snapshot = null
      await this._fetchRoom(true)
    } catch (error) {
      temp.remove()
      if (!input.value) input.value = body
      this._toast(error.message || this.t.error_send, "error")
    } finally {
      this.sending = false
    }
  }

  // A reply to ONE person (or a "/note" for the team).
  async _sendReply(body) {
    const thread = this.thread
    this.sending = true
    const input = this.ctl.inputTarget
    const temp = this._threadBubble({ id: 0, team: true, pending: true, name: this.t.you || "", initials: "•",
                                      color: this.ctl.currentUserIdValue, body: body, at_label: this.t.sending || "" })
    this.ctl.listTarget.appendChild(temp)
    this.ctl._scrollDown()
    input.value = ""
    try {
      const resp = await fetch(this.ctl._url("mass_text_reply"), {
        method: "POST", headers: this.ctl._headers(), credentials: "same-origin",
        body: JSON.stringify({ post_id: thread.post_id, entry_id: thread.entry_id, body: body })
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok || !data.ok) throw new Error(data.error || this.t.error_send)
      this.snapshot = null
      if (this.thread === thread) await this._fetchThread(true)
    } catch (error) {
      temp.remove()
      if (!input.value) input.value = body
      this._toast(error.message || this.t.error_send, "error")
    } finally {
      this.sending = false
    }
  }

  // ── Little helpers ─────────────────────────────────────────────────────
  _initialsAvatar(initials, colorId, size = "w-8 h-8") {
    return `<div class="${size} rounded-full shrink-0 flex items-center justify-center text-white text-[13px] font-bold" style="background:${this.ctl._color(Number(colorId) || 0)}">${this._esc(initials || "?")}</div>`
  }

  _format(text) {
    return this._esc(text || "").replace(/\n/g, "<br>")
  }

  _esc(value) {
    return this.ctl._esc(value)
  }

  // House toast (flash controller), like fee_schedule_controller's.
  _toast(message, type = "notice") {
    const kind = type === "error" ? "alert" : "notice"
    const el = document.createElement("div")
    el.className = `flash-toast-${kind}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const icon = document.createElement("div")
    icon.className = "shrink-0"
    icon.innerHTML = kind === "notice"
      ? '<svg fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const text = document.createElement("p")
    text.className = "flex-1 text-sm"
    text.textContent = message
    el.append(icon, text)
    document.body.prepend(el)
  }
}
